import React, { Suspense, useContext, useEffect, useRef } from 'react';
import './App.scss';
import { Route, BrowserRouter, Navigate, Routes, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Loading from 'components/Login/Loading';
import Initialise from 'components/Login/Initialise';
import NeedsVerification from 'components/Login/NeedsVerification';
import SubscriptionSuccess from 'components/Login/SubscriptionSuccess';
import { StateProvider, store } from 'components/Store';
import ReactNotification from 'react-notifications-component';
import useTrackingCodes from 'components/useTrackingCodes';
import Register from 'components/Login/Register';
import AcceptInvite from 'components/Login/AcceptInvite';
import { EndOfTrial } from 'components/Login/EndOfTrial';
import PricingPage from 'components/Pricing/PricingPage';
import CheckPlan from 'components/Settings/CheckPlan';
import CreateProfile from 'components/Login/CreateProfile';
import LoginSuccess from 'components/Login/LoginSuccess';
import EmbedPage from 'components/embed';
import * as Sentry from '@sentry/react';
import Clarity from 'utils/clarity';
import InfluenceAddContent from 'components/Dashboard/UserContent/InfluenceAddContent';
import mobileCheck from 'components/Dashboard/helpers/mobileCheck';
import RegistrationVerification from 'components/Login/RegistrationVerification';
import Dashboard from 'components/Dashboard';
import InfluenceCreate from 'components/Dashboard/UserContent/InfluenceCreateContainer';
import SavedItems from 'components/SavedItems';
import GimmickPopUp from 'components/Dashboard/components/GimmickPopup';
import SentimentComponent from 'components/Dashboard/Sentiment';
import SharePage from 'components/Dashboard/UserContent/ShareUserContentPage';
import ShareDocumentPage from 'components/ShareDocument/ShareDocumentPage';
import InfluenceAIContainer from 'components/Dashboard/UserContent/InfluenceAIContainer';
import ReferralCode from 'components/Dashboard/ReferralCode/ReferralCode';
import EmbedAnalytics from 'components/EmbedAnalytics';
import OauthCallback from 'components/Settings/SlackIntegration/OauthCallback';
import SlackIntegration from 'components/Dashboard/SlackIntegration/SlackIntegration';
import Modal from 'react-modal';
import EmailVerificationSucccess from 'components/Dashboard/EmailVerificationSucccess';
import StakeHolders from 'components/Dashboard/StakeHolders/index';
import NoAllowedReference from 'components/Dashboard/KeyUpdates/NoAllowedReference';
import ListRoutes from 'components/Dashboard/ListRoutes';
import InfluenceContainer from 'components/Dashboard/Influence/index';
import Redirections, { RedirectionsNoAuthenticated } from './Routes/Redirections';
import WritingAssitant from 'components/WritingAssistant/index';
import ContentRoutes from 'components/Dashboard/ContentRoutes';
import normalize from 'components/Dashboard/utilities/normalizeString';
import CalendarList from 'components/Dashboard/UpcomingEvents/CalendarList';
import useContentMainRoutesMapping from './Routes/ContentMainRoutes';
import Campaigns from 'components/Dashboard/Campaigns';
import useInfluenceRoutesMapping from './Routes/useInfluenceRoutesMapping';
import Election2024 from 'components/Temporary/Election2024';
import { FiltersProvider } from 'components/Dashboard/Filters/FiltersContext';
import removeLocalStorage from 'components/Dashboard/Navigation/RemoveLocalStorage';
import DataHubRedirect from './Routes/DataHubRedirect';
import LiveFromParliament from 'components/Dashboard/LiveFromParliament/LiveFromParliament';
import useAppFunctions, { intercomConditional } from 'components/useAppFunctions';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import BriefPolicyMakersContainer from 'components/Dashboard/Influence/BriefPolicymakers';
import TeamContent from 'components/Settings/TeamContent';
import EngagementStatistics from 'components/Dashboard/Influence/EngagementStatistics/EngagementStatistics';
import getAppSite from 'utils/getAppSite';
import injectHreflangTags from 'utils/injectHreflangTags';
import DashboardComponent from 'components/Dashboard/DashboardComponent';
import useDashboardUrlCondition from 'components/Dashboard/Navigation/useDashboardUrlCondition';
import useResearchItemsUrl from 'components/Dashboard/Navigation/useResearchItemsUrl';
import NotesPage from 'components/Dashboard/NotesPage/NotesPage';
import { ContentResultsProvider } from 'components/Dashboard/ContentResultsContext';
import ManifestosTracker from 'components/Temporary/Manifestos/ManifestosTracker';
import { unregisterServiceWorker } from 'components/Settings/EmailAlerts/pushServiceWorkerUtils';

const lazyWithPreload = (factory) => {
  let promise = null;
  const load = () => {
    if (!promise) promise = factory();
    return promise;
  };
  const Component = React.lazy(load);
  Component.preload = load;
  return Component;
};

const Home = lazyWithPreload(() => import('components/home/home'));
const Reports = lazyWithPreload(() => import('components/Reports/index'));
const Analytics = lazyWithPreload(() => import('components/Dashboard/Analytics/index'));
const Admin = lazyWithPreload(() => import('components/Admin'));
const Settings = lazyWithPreload(() => import('components/Settings'));

const App = () => {
  const { isAuthenticated, isLoading, error, user, loginWithRedirect } = useAuth0();
  Modal.setAppElement('#root');

  useEffect(() => {
    if (isAuthenticated && user !== undefined) {
      var userId = `${user.sub}`;
      var emailAddress = `${user.email}`;

      Sentry.setUser({ username: userId, email: emailAddress });

      if (window.hj !== undefined) {
        window.hj('identify', userId, {
          email: emailAddress,
        });
      }

      if (import.meta.env.VITE_APP_CLARITY_ID) {
        Clarity.identify(userId, null, null, emailAddress);
        Clarity.setTag('Auth0ID', userId);
        Clarity.setTag('Email', emailAddress);
      }
    }
    if (mobileCheck() && intercomConditional) {
      //method from Intercom to hide the button on mobile
      window.Intercom('update', {
        hide_default_launcher: true,
      });
    }
  }, [user, isAuthenticated]);

  // TC: Do not run this code if we are a bot as it will result in the page not rendering for the bot
  if (!/bot|google|baidu|bing|msn|teoma|slurp|yandex/i.test(navigator.userAgent)) {
    if (error) {
      // if it is a state mismatch error, run the login again
      // with the react SDK using the universal login.
      //
      // As there is an active session the user will log in seamlessly without
      // the need to present credentials.
      loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${import.meta.env.VITE__APPURL}/login-success`,
        },
      });
    }
    if (isLoading) {
      return <Loading />;
    }
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <StateProvider>
          <FiltersProvider>
            <ContentApp />
          </FiltersProvider>
        </StateProvider>
      </Suspense>
    </BrowserRouter>
  );
};

const ContentApp = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state;
  const { enableStakeholders } = activePlan ?? {};

  const { isAuthenticated } = useAuth0();
  const { InfluenceRoutesMapping, createParameters } = useInfluenceRoutesMapping();
  const { ContentMainRoutesMapping } = useContentMainRoutesMapping();

  const location = useLocation();

  const { isLoading } = useAppFunctions();
  const { defaultUrl, allowedDashboardUsers } = useDashboardUrlCondition();
  const { policymakerUrl } = useResearchItemsUrl();
  useTrackingCodes();

  useEffect(() => {
    const isAllowedPath =
      location.pathname === '/' || location.pathname === '/pricing' || location.pathname.startsWith('/data-hub');
    if (!isAuthenticated && isAllowedPath) {
      injectHreflangTags();
    } else {
      document.head.querySelectorAll('link[data-pm-hreflang]').forEach((el) => el.remove());
    }
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    const preload = () => {
      Home.preload();
      Reports.preload();
      Analytics.preload();
      Admin.preload();
      Settings.preload();
    };
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(preload);
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(preload, 200);
    return () => clearTimeout(id);
  }, []);

  return (
    <>
      <ReactNotification />
      {isLoading ? (
        <Loading />
      ) : (
        <Routes>
          {Redirections(location)}
          {!isAuthenticated && RedirectionsNoAuthenticated(location)}
          <Route
            path='/'
            element={isAuthenticated ? <Navigate to={`/${defaultUrl}${window.location.search}`} /> : <Home />}
          ></Route>
          <Route path='/integration-with-slack' element={SlackIntegration} />
          <Route path='/email-verification-success' element={<EmailVerificationSucccess />} />
          <Route path='/login-success' element={<LoginSuccess />} />
          <Route path='/needs-verification' element={<NeedsVerification />} />
          <Route path='/subscription-success' element={<SubscriptionSuccess />} />
          <Route path={'/create-your-profile/*'} element={<CreateProfile />}>
            <Route path=':step' element={<CreateProfile />} />
          </Route>
          <Route path='/initialise' element={<Initialise />} />
          <Route path='/accept-invite' element={<AcceptInvite />} />
          <Route path='/register' element={<Register />} />
          <Route path='/login' element={<Register login={true} />}></Route>
          <Route
            path='/registration-verification'
            element={isAuthenticated ? <Navigate replace to={`/`} /> : <RegistrationVerification />}
          ></Route>
          <Route
            path='/referral-registration-verification'
            element={isAuthenticated ? <Navigate replace to={`/`} /> : <RegistrationVerification />}
          ></Route>
          <Route path='/end-of-trial' element={<EndOfTrial />} />
          <Route path='/end-of-subscription' element={<EndOfTrial />} />
          <Route path='/sentiment' element={<SentimentComponent />} />
          <Route path={'/data-hub/*'} element={<DataHubRedirect />}>
            <Route path={'*'} element={<Analytics />} />
          </Route>
          <Route
            path='/live-from-parliament'
            element={
              isAuthenticated ? <Navigate replace to={`/monitor-inbox/parliament-live`} /> : <LiveFromParliament />
            }
          />
          <Route exact path='/general-election-2024/*' element={<Dashboard />}>
            <Route path={'*'} element={<Election2024 />} />
          </Route>
          <Route exact path='/devolved-elections-2026/' element={<Dashboard />}>
            <Route path={'*'} element={<ManifestosTracker />} />
          </Route>

          <Route path='/logout' element={<Logout />} />
          <Route path='/slack-oauth-callback' element={<OauthCallback />} />
          <Route path={`/pricing`} element={<PricingPage />} />
          <Route path={`/admin/*`} element={<Admin />} />

          <Route path={'/saved-items/*'} element={<Dashboard />}>
            <Route path={``} element={<SavedItems />} />
            <Route path={`:id(\\d+)`} element={<SavedItems />} />
          </Route>

          <Route path={`/settings/check-plan`} element={<CheckPlan />} />
          <Route path='/settings/*' element={<Settings />}>
            <Route path='*' element={<Settings />} />
          </Route>
          <Route path='/brief-policymakers/ai/:title' element={<InfluenceAIContainer />} />
          <Route path='/brief-policymakers/:page/create/:step' element={<InfluenceCreate />} />
          <Route path='/brief-policymakers/:page/edit/:id/:step' element={<InfluenceCreate />} />
          <Route path='/brief-policymakers/add-content' element={<InfluenceAddContent />} />

          <Route
            path='/policymakers'
            element={
              parliamentarianUsers(activePlan?.code) ? (
                <Navigate replace to={`${policymakerUrl}${location.hash ?? ''}`} />
              ) : (
                <Navigate replace to='/people/stakeholder-mapping' />
              )
            }
          />
          <Route
            path={'/policymakers/*'}
            element={
              parliamentarianUsers(activePlan?.code) ? (
                <Dashboard />
              ) : (
                <Navigate replace to='/people/stakeholder-mapping' />
              )
            }
          >
            <Route path='*' element={<InfluenceContainer />}>
              <Route path='' element={<StakeHolders />} />
            </Route>
          </Route>
          <Route path='/notes' element={<Navigate to='/notes/my-own' />}></Route>
          <Route path={`/notes/*`} element={<Dashboard />}>
            <Route index path={':notesAuthor'} element={<NotesPage />} />
          </Route>
          <Route path={`/people/*`} element={<Dashboard />}>
            {Object.keys(InfluenceRoutesMapping).map((section) => {
              const { element, parameters } = InfluenceRoutesMapping[section];
              return (
                <Route key={`route-${section}`} path={`${section}/*`} element={<InfluenceContainer />}>
                  <Route index path={'*'} element={element} />
                  {parameters && <>{createParameters(parameters, element)}</>}
                </Route>
              );
            })}
            <Route
              index
              element={
                <Navigate
                  replace
                  to={`${enableStakeholders ? `/people/directory` : '/brief-policymakers/lobbying-material'}${
                    location.search
                  }${location.hash}`}
                />
              }
            ></Route>
          </Route>
          <Route
            path={`/brief-policymakers/*`}
            element={getAppSite() === 'usa' ? <Navigate replace to='/' /> : <Dashboard />}
          >
            <Route path={`lobbying-material/*`} element={<BriefPolicyMakersContainer />}>
              <Route index path={'*'} element={<TeamContent />} />
            </Route>
            <Route path={`engagement-statistics/*`} element={<BriefPolicyMakersContainer />}>
              <Route index path={'*'} element={<EngagementStatistics />} />
              <Route path={':type/:itemId'} element={<EngagementStatistics />} />
            </Route>
            <Route
              index
              element={
                <Navigate replace to={`/brief-policymakers/lobbying-material${location.search}${location.hash}`} />
              }
            ></Route>
          </Route>
          <Route path='/share-document/:code' element={<ShareDocumentPage />} />
          <Route path='/share/:type/:id' element={<SharePage />} />
          <Route path='/r/:referralCode' element={<ReferralCode />} />

          <Route path={`/reference/upgrade`} element={<NoAllowedReference />} />
          <Route path={'/calendar/*'} element={<Dashboard />}>
            <Route path=':startdate' element={<CalendarList />} />
            <Route path={'*'} element={<CalendarList />} />
          </Route>
          <Route path={'/campaigns/*'} element={<Dashboard />}>
            <Route path={'*'} element={<Campaigns />} />
          </Route>

          <Route path={'/analytics/*'} element={<Dashboard />}>
            <Route path={'*'} element={<Analytics />} />
          </Route>
          <Route path={'/ai-assistant/*'} element={<Dashboard />}>
            <Route
              index
              path={'*'}
              element={parliamentarianUsers(activePlan?.code) ? <WritingAssitant /> : <Navigate to='/monitor-inbox' />}
            />
          </Route>
          <Route path={'/reports/*'} element={<Dashboard />}>
            <Route index path={'*'} element={<Reports />} />
          </Route>

          <Route path='/dashboard' element={allowedDashboardUsers ? <Dashboard /> : <Navigate to='/monitor-inbox' />}>
            <Route index path={':date'} element={<DashboardComponent />} />
            <Route index path={'*'} element={<DashboardComponent />} />
          </Route>

          {Object.keys(ContentMainRoutesMapping).map((section) => {
            const { element, parameters } = ContentMainRoutesMapping[section];
            return (
              <Route key={`${normalize(section)}`} path={`/${section}/*`} element={<Dashboard />}>
                <Route path={`*`} element={<ContentRoutes />}>
                  {parameters ? (
                    <>{createParameters(parameters, element)}</>
                  ) : (
                    <>
                      <Route path={`:id`} element={<ContentRoutes />} />
                      <Route path={`:id/:title`} element={element} />
                    </>
                  )}
                </Route>
              </Route>
            );
          })}
          <Route path='/:mainSection/*' element={<Dashboard />}>
            <Route path='*' element={<ListRoutes />} />
          </Route>
          <Route element={<NoMatchPage />} />
        </Routes>
      )}
      <GimmickPopUp />
    </>
  );
};

const NoMatchPage = () => {
  return <h3 className='px-5'>404 - Not found</h3>;
};

const Logout = () => {
  const { logout } = useAuth0();

  const logoutWithRedirect = () => logout({ logoutParams: { returnTo: window.location.origin } });
  const logoutFunction = useRef(null);
  logoutFunction.current = () => {
    logoutWithRedirect();
    removeLocalStorage();
    unregisterServiceWorker();
  };

  useEffect(() => {
    logoutFunction.current();
  }, []);
  return <Loading />;
};

const EmbedApp = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <StateProvider>
          <ContentResultsProvider>
            <ReactNotification />
            <Routes>
              <Route path='/embed' element={<EmbedPage />} />
              <Route path='/embed-analytics' element={<EmbedAnalytics />} />
            </Routes>
          </ContentResultsProvider>
        </StateProvider>
      </Suspense>
    </BrowserRouter>
  );
};

export { EmbedApp };
export default App;
